import {useEffect} from "react"

import cn from 'clsx'

import { getValueWithCurrency } from '@App/helpers/toCurrency'
import {useAppDispatch, useAppSelector} from "@App/hooks"
import { MarketIcon } from '@App/ui/crypto_exchange/desktop/components/common/MarketIcon'
import { ToolTip } from '@App/ui/crypto_exchange/desktop/components/controls/ToolTip'


import {
  asyncGetTradeTransactions,
  TradeTransaction,
  Transaction
} from "@Redux/slices/reports/ReportsSlice";
import {allTradeTransactions, allTransactions} from "@Redux/slices/reports/selectors";
import {currentWallet} from "@Redux/slices/user/selectors";

import {
  formatDateFromApi,
  formatTimeFromApi, getTimestampFromPeriod,
  ReportsFilter,
  useCopyText
} from '../../model'

import s from './styles.module.scss'

const columns: { title: string; width: string }[] = [
  { title: 'Date', width: '4%' },
  { title: 'Time', width: '2%' },
  { title: 'Market/ Size', width: '10%' },
  { title: 'Type', width: '9%' },
  { title: 'Triggered by', width: '15%' },
  { title: 'Price', width: '8%' },
  { title: 'Result', width: '3%' },
  { title: 'ID', width: '1%' },
]

const types = {
  bet: "Trade Opened",
  win: "Trade Closed"
}

const calculateNegative = (transaction: TradeTransaction) => {
  return (transaction?.trade.action_type === 'buy' && transaction.type === 'win') ||
    (transaction?.trade.action_type === 'sell' && transaction.type === 'bet')
}

export const TradesTable = ({ onRowClick, filters}: { onRowClick?: (val: Transaction, title: string) => void, filters: ReportsFilter }) => {
  const tradeTransactions = useAppSelector(allTradeTransactions)
  const wallet = useAppSelector(currentWallet)
  const dispatch = useAppDispatch()

  const timestampFrom = getTimestampFromPeriod(filters.period)
  const args = {
    timestampInterval: {
      timestampFrom: timestampFrom,
      timestampTo:   null
    }
  }
  useEffect(() => {
    if (!wallet?.id) return
    dispatch(asyncGetTradeTransactions(args))
  }, [wallet?.id, filters])

  const { copyText } = useCopyText()

  return (
    <div className={s.tableContainer}>
      <table className={s.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th style={{ width: col.width }} key={col.title} className={s[col.title.toLocaleLowerCase()]}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {tradeTransactions.map((transaction, index) => (
            <tr
              className={cn(s.row, s.fadeInRow)}
              key={transaction.trade.id}
              style={{ animationDelay: `${index * 40}ms` }}
              onClick={() => onRowClick?.(transaction, types[transaction.type as keyof typeof types])}>
              <td className={s.col}>{formatDateFromApi(transaction.transaction_time)}</td>
              <td className={s.col}>{formatTimeFromApi(transaction.transaction_time)}</td>
              <td className={cn(s.col, s.info)}>
                <MarketIcon symbol={transaction.market.symbol} />{' '}
                {transaction.market.title}
                <span className={cn(s.quantity, !calculateNegative(transaction) && s.buy)}>
                  {(calculateNegative(transaction) ? "-" : "+")  + transaction.trade.quantity}
                </span>
              </td>
              <td className={s.col}>{types[transaction.type as keyof typeof types]}</td>
              <td className={s.col}>user</td>
              <td className={s.col}>{transaction.trade.open_rate}</td>
              <td className={cn(s.col, s.result, transaction.trade.result < 0 && s.negative)}>
                {transaction.trade.status !== "open" && getValueWithCurrency(transaction.trade.currency, transaction.trade.result)}
              </td>
              <td className={s.col}>
                <ToolTip content={<span className={s.toolTip}>{transaction.trade.front_id}</span>}>
                  <span className={s.idBadge} onClick={() => copyText(transaction.trade.front_id)}>
                    ID
                  </span>
                </ToolTip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
